import { BasePersonalFinancialStatementResource, PersonalFinancialStatementFetchOptions, PersonalFinancialStatementResourceType } from './basePersonalFinancialStatementResource'
import { getTopMerchantsForDateRange } from '@/services/personalFinancialStatement'
import { log } from '@/utils/logger'
import { RecordSnapshotRetrievalStrategy, TopMerchantsByTotalAmountDescendingData, TopMerchantsDuration } from 'aven_types'
import { assertNever } from 'aven_shared/src/utils/assertUtils'

const SUPPORTED_DURATIONS = [TopMerchantsDuration.PAST_WEEK] // TODO: When we add support for more durations, add them here

export class PersonalFinancialStatementTopMerchantsResource extends BasePersonalFinancialStatementResource<PersonalFinancialStatementResourceType.TOP_MERCHANTS> {
    constructor() {
        super(PersonalFinancialStatementResourceType.TOP_MERCHANTS)
    }

    protected async fetchData(options?: PersonalFinancialStatementFetchOptions): Promise<Record<TopMerchantsDuration, TopMerchantsByTotalAmountDescendingData | null> | null> {
        const now = new Date()

        const fetchPromises = SUPPORTED_DURATIONS.map(async (duration) => {
            const startDate = this.getStartDateForDuration(duration)

            log.info('PersonalFinancialStatementTopMerchantsResource.fetchData: Fetching %{duration} from %{startDate} to %{now}', { duration, startDate, now })

            const response = await getTopMerchantsForDateRange({
                dateRange: {
                    startDate,
                    endDate: now,
                },
                sourceUniqueId: null,
                retrievalStrategy: RecordSnapshotRetrievalStrategy.SINGLE_SOURCE_WATERFALL_HIGHEST_RECORD_COUNT,
                rateLimitingOptions: {
                    turnOffRateLimiter: false,
                    onlyCountSuccessfulFetchesWhenRateLimiting: true,
                },
                fetchNewRecordSnapshotsOptions: {
                    doNotFetchNewRecordsFromSource: false,
                    fetchKeyStatusesFromSourceValues: true,
                    onlyFetchBetweenMostRecentSnapshotAndCurrentDate: options?.onlyFetchBetweenMostRecentSnapshotAndCurrentDate ?? true,
                    fetchSnapshotDateRangeOverride: null,
                },
            })

            if (response.data.success && response.data.payload.topMerchantData.length > 0) {
                log.info('PersonalFinancialStatementTopMerchantsResource.fetchData: Fetched %{count} top merchants for %{duration}', { count: response.data.payload.topMerchantData.length, duration })
                return { duration, data: response.data.payload }
            } else {
                log.warn('PersonalFinancialStatementTopMerchantsResource.fetchData: No data returned from API for %{duration}', { duration })
                return { duration, data: null }
            }
        })

        const responses = await Promise.all(fetchPromises)

        const result: Record<TopMerchantsDuration, TopMerchantsByTotalAmountDescendingData | null> = {
            [TopMerchantsDuration.PAST_WEEK]: null,
            [TopMerchantsDuration.PAST_MONTH]: null,
            [TopMerchantsDuration.PAST_YEAR]: null,
        }

        for (const { duration, data } of responses) {
            result[duration] = data
        }

        const hasAnyData = Object.values(result).some((data) => data !== null)
        return hasAnyData ? result : null
    }

    private getStartDateForDuration(duration: TopMerchantsDuration): Date {
        const now = new Date()
        switch (duration) {
            case TopMerchantsDuration.PAST_WEEK:
                return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
            case TopMerchantsDuration.PAST_MONTH:
                return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
            case TopMerchantsDuration.PAST_YEAR:
                return new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000)
            default:
                return assertNever(duration, `Unsupported TopMerchantsDuration: ${duration}`)
        }
    }
}
